
import { defineComponent } from '@nuxtjs/composition-api';
import { mapState } from 'vuex';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import PublicPortfolioHeroMenu from '@/components/partials/public-portfolio/PublicPortfolioHeroMenu/PublicPortfolioHeroMenu.vue';
import { publicFieldNames } from '@/components/partials/public-portfolio/constants';
import PublicPortfolioConnectionActionsMixin from '@/components/partials/public-portfolio/PublicPortfolioConnectionActions/PublicPortfolioConnectionActionsMixin';
import PublicPortfolioHeroDataMixin from '@/components/partials/public-portfolio/PublicPortfolioHero/mixins/publicPortfolioHeroDataMixin';

interface ContactButton {
  label: string;
  icon: string;
  configurationFieldName: string;
}

interface ContactButtonPublic extends ContactButton {
  attrs?: {
    href: string;
    target: string;
    rel: string;
  };
}

const CONTACT_BUTTONS: ContactButton[] = [
  {
    label: 'phone number',
    icon: 'phone',
    configurationFieldName: publicFieldNames.FIELD_PHONE_IS_PUBLIC,
  },
  {
    label: 'email',
    icon: 'email',
    configurationFieldName: publicFieldNames.FIELD_PUBLIC_EMAIL_IS_PUBLIC,
  },
  {
    label: 'linkedin',
    icon: 'linkedin',
    configurationFieldName: publicFieldNames.FIELD_LINKEDIN_SLUG_IS_PUBLIC,
  },
  {
    label: 'calendar',
    icon: 'calendar',
    configurationFieldName: publicFieldNames.FIELD_CALENDAR_LINK_IS_PUBLIC,
  },
];

export default defineComponent({
  name: 'PublicPortfolioHeroContacts',
  components: {
    BravadoButtonV3,
    PublicPortfolioHeroMenu,
  },
  mixins: [PublicPortfolioHeroDataMixin, PublicPortfolioConnectionActionsMixin],
  computed: {
    ...mapState('portfolio', ['contacts']),
    isContactsDisabled() {
      return !this.isConnected && !this.isCurrentUserPortfolio;
    },
    isAwaitingAcception() {
      return Boolean(this.portfolioConnectionRequests.incomingConnectionRequest);
    },
    contactButtons(): ContactButtonPublic[] {
      return CONTACT_BUTTONS.reduce(
        (acc: ContactButtonPublic[], button) =>
          this.isContactFieldPublic(button.configurationFieldName)
            ? [
                ...acc,
                {
                  ...button,
                  attrs: this.isContactsDisabled
                    ? undefined
                    : {
                        href: this.getContactFieldLink(button.configurationFieldName),
                        rel: 'nofollow noopener noreferrer',
                        target: '_blank',
                      },
                },
              ]
            : acc,
        [],
      );
    },
  },
  methods: {
    isContactFieldPublic(fieldName: string): boolean {
      if (!this.publicPortfolioUser.configuration[fieldName]) return false;
      if (!this.contacts) return false;

      switch (fieldName) {
        case publicFieldNames.FIELD_PHONE_IS_PUBLIC:
          return Boolean(this.contacts.phone);
        case publicFieldNames.FIELD_PUBLIC_EMAIL_IS_PUBLIC:
          return Boolean(this.contacts.email);
        case publicFieldNames.FIELD_LINKEDIN_SLUG_IS_PUBLIC:
          return Boolean(this.contacts.linkedin_url) && (this.isCurrentUserPortfolio || !this.$auth.loggedIn);
        case publicFieldNames.FIELD_CALENDAR_LINK_IS_PUBLIC:
          return Boolean(this.contacts.calendar_link);
        default:
          return false;
      }
    },
    getContactFieldLink(fieldName: string): string {
      switch (fieldName) {
        case publicFieldNames.FIELD_PHONE_IS_PUBLIC:
          return `tel:${this.contacts.phone}`;
        case publicFieldNames.FIELD_PUBLIC_EMAIL_IS_PUBLIC:
          return `mailto:${this.contacts.email}`;
        case publicFieldNames.FIELD_LINKEDIN_SLUG_IS_PUBLIC:
          return this.contacts.linkedin_url;
        case publicFieldNames.FIELD_CALENDAR_LINK_IS_PUBLIC:
          return this.contacts.calendar_link;
        default:
          return '';
      }
    },
    async handleConfirm(button: ContactButtonPublic) {
      if (!this.isContactsDisabled || this.isRequestSent) return;

      try {
        await this.$confirm.open({
          title: this.isAwaitingAcception
            ? `Accept connection to see ${button.label}`
            : `Connect to see ${button.label}`,
          hint: `Connect with ${this.publicPortfolioUser.firstname} to contact them.`,
          action: this.isAwaitingAcception ? 'Accept' : 'Connect',
          variant: 'primary',
        });
        if (this.isAwaitingAcception) {
          this.$analytics.logEvent('public-portfolio__modal__accept_button_click', { action: button.label });
          await this.acceptConnectionRequest();
        } else {
          this.$analytics.logEvent('public-portfolio__modal__request_button_click', { action: button.label });
          await this.sendConnectionRequest();
        }
      } catch (e) {
        this.$analytics.logEvent('public-portfolio__modal__cancel_button_click', { action: button.label });
        this.$sentryUtils.error(e);
      }
    },
  },
});
